import { createSlice } from "@reduxjs/toolkit";
import axios from "axios"; // this does not include app headers
import { dispatch } from "../index";
import axiosServices from "../../utils/axios";
import { storage } from "../../utils/helpers/storage";

// ----------------------------------------------------------------------

const parseVersion = (version) => {
  // Handle both string input and object input for backwards compatibility
  const versionString = typeof version === 'string' ? version : version.version;
  const result = versionString
    .split('.')
    .map(num => parseInt(num, 10) || 0); // Convert version parts to numbers
  return result;
};

const compareVersions = (a, b) => {
  const versionA = parseVersion(a.version);
  const versionB = parseVersion(b.version);

  for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
    const numA = versionA[i] || 0; // Default to 0 if missing
    const numB = versionB[i] || 0;
    if (numA !== numB) {
      return numB - numA; // Descending order
    }
  }
  return 0;
};

// Compare two version strings directly
const isVersionGreater = (versionA, versionB) => {
  const parsedA = parseVersion(versionA); // Fixed: pass string directly, not object
  const parsedB = parseVersion(versionB); // Fixed: pass string directly, not object
  
  for (let i = 0; i < Math.max(parsedA.length, parsedB.length); i++) {
    const numA = parsedA[i] || 0;
    const numB = parsedB[i] || 0;
    if (numA > numB) {
      return true;
    }
    if (numA < numB) {
      return false;
    }
  }
  return false;
};

export const getLatestRelease = (releases = []) => {
  if (!Array.isArray(releases) || releases.length === 0) {
    return null; // Return null safely if the input is not an array or is empty
  }

  // Sort releases based ONLY on version
  const sortedReleases = releases
    .filter(release => release.version) // Ensure version exists
    .sort(compareVersions);

  return sortedReleases[0] || null; // Return the latest release or null if none exist
};


export const initialState = {
  error: null,
  releases: [],
  majorVersions: [],
  lastRelease: null,
  showNewRelease: false,
  healthCheckToken: null,
  healthCheckPaths: [],
  serviceNames: {
    auth: "Authorization Service",
    activity: "Activity Service",
    analytics: "Analytics Service",
    config: "Configuration Service",
    notifications: "Notification Service",
    services: "General Service",
    tooling: "Tooling Service",
  },
};

const slice = createSlice({
  name: "help",
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },
    getReleaseInfoSuccess(state, action) {
      state.releases = action.payload;
    },
    getMajorVersionsSuccess(state, action) {
      state.majorVersions = action.payload;
    },
    getAllReleaseSuccess(state, action) {
      const latestVersion = action.payload?.version;
      const storedVersion = storage.getLastRelease();
      
      // Show new release indicator if:
      // 1. No stored version (first time user)
      // 2. Latest version is greater than stored version
      let shouldShowNewRelease = false; // Changed default to false - only show if there's actually a new release
      
      try {
        if (!storedVersion || storedVersion === "0.0.0") {
          shouldShowNewRelease = true;
        } else if (latestVersion) {
          shouldShowNewRelease = isVersionGreater(latestVersion, storedVersion);
        } else {
          shouldShowNewRelease = false;
        }
      } catch (error) {
        shouldShowNewRelease = false; // Changed default to false on error
      }
      
      state.showNewRelease = shouldShowNewRelease;
      state.lastRelease = latestVersion;
    },
    getLastReleaseSuccess(state, action) {
      const latestVersion = action.payload?.version;
      
      // User visited the release page, mark this version as seen
      state.showNewRelease = false;
      storage.setLastRelease(latestVersion);
      state.lastRelease = latestVersion;
    },
    getHealthCheckTokenSuccess(state, action) {
      state.healthCheckToken = action.payload;
    },
    getHealthCheckPathsSuccess(state, action) {
      state.healthCheckPaths = action.payload;
    },
  },
});
// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

// store version in cookie
// do on user login
// nothing saved in coookie - show dot
// same no dot until get to page
// version # / have seen page
export function getMajorVersions() {
  return async (dispatch) => {
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/release/version`
      );
      const versions = (response.data?.majorVersions ?? []).map((v) => v.majorVersion);
      dispatch(slice.actions.getMajorVersionsSuccess(versions));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getReleaseInfo(majorVersion) {
  return async () => {
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/release/find?majorVersion=${majorVersion}`,
        {}
      );
      dispatch(slice.actions.getReleaseInfoSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllReleaseInfo() {
  return async (dispatch) => {
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/release`
      );

      const latestRelease = getLatestRelease(response.data?.rows || []);

      if (latestRelease) {
        dispatch(slice.actions.getAllReleaseSuccess(latestRelease));
      } else {
        dispatch(slice.actions.hasError(new Error('No valid releases found')));
      }

    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getLastReleaseInfo() {
  return async (dispatch) => {
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/release`
      );

      const latestRelease = getLatestRelease(response.data?.rows || []);

      if (latestRelease) {
        dispatch(slice.actions.getLastReleaseSuccess(latestRelease));
      } else {
        dispatch(slice.actions.hasError(new Error('No valid releases found')));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getServiceHealthToken() {
  return async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/activity/activity/service/health`,
        {},
        {}
      );
      dispatch(slice.actions.getHealthCheckTokenSuccess(response.data.token));
      dispatch(
        slice.actions.getHealthCheckPathsSuccess(response.data.services)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getServiceHealth(token, service) {
  return async () => {
    try {
      return await axios.get(
        `https://management.azure.com/${service.path}/providers/Microsoft.ResourceHealth/availabilityStatuses/current?api-version=2022-10-01`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
